import './aws-config';
import './App.css';
import React, { useEffect, useState } from 'react';
import { withAuthenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { signOut, fetchAuthSession } from 'aws-amplify/auth';
import Freight from './Freight';
import VesselDetail from './VesselDetail';
import Chartering from './Chartering';
import WeatherRisk from './WeatherRisk';
import Trades from './Trades';
import Deals from './Deals';

const API_URL = "https://ltwph7gpvb.execute-api.us-east-1.amazonaws.com/vessels";

// Sidebar structure: single items, or groups with items inside.
const NAV = [
  { id: "dashboard", label: "Dashboard", title: "Dashboard" },
  {
    group: "Vessels",
    items: [
      { id: "vessels", label: "Vessel Intelligence", title: "Vessel Intelligence" },
      { id: "freight", label: "Freight Calculator", title: "Freight & Voyage Calculator" },
      { id: "charter", label: "Chartering", title: "Chartering & Vessel Matcher" },
      { id: "weather", label: "Weather Risk", title: "Weather & Route Risk" }
    ]
  },
  {
    group: "Trade",
    items: [
      { id: "deals", label: "Deal Room", title: "Deal Room: Terms, LCs, Documents & Money" },
      { id: "ops", label: "Trade Operations", title: "Trade Operations & Documents" }
    ]
  }
];

const ALL_PAGES = NAV.flatMap((n) => (n.items ? n.items : [n]));
const EXTERNAL_GROUPS = ["seller", "buyer", "financier"];
// Outside parties (seller, buyer, financier) only see the Deal Room.
const EXTERNAL_NAV = [{ id: "deals", label: "Deal Room", title: "Deal Room: Terms, LCs, Documents & Money" }];

const badgeClass = (s) =>
  s === "In Transit" ? "badge transit" : s === "Anchored" ? "badge anchored" : "badge other";

function VesselTable({ vessels, onOpen }) {
  return (
    <div className="tablewrap">
      <table>
        <thead>
          <tr><th>Vessel</th><th>Type</th><th>Flag</th><th>Built</th><th>Status</th><th>Location</th></tr>
        </thead>
        <tbody>
          {vessels.map((v) => (
            <tr key={v.id}>
              <td><button className="link" onClick={() => onOpen(v.id)}>{v.name}</button></td>
              <td>{v.type}</td>
              <td>{v.flag}</td>
              <td>{v.built}</td>
              <td><span className={badgeClass(v.status)}>{v.status}</span></td>
              <td>{v.location}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const authComponents = {
  Header() {
    return (
      <div className="login-intro">
        <div className="login-logo">⚓</div>
        <h1>MMNY Shipping Management Suite</h1>
        <p>Vessels, freight, chartering and trade operations in one place.</p>
        <ul>
          <li>Vet vessels with risk scores and port history</li>
          <li>Estimate voyages and match open vessels to your cargo</li>
          <li>Track trades, tasks and documents end to end</li>
        </ul>
      </div>
    );
  }
};

function App() {
  const [page, setPage] = useState("dashboard");
  const [selected, setSelected] = useState(null);
  const [vessels, setVessels] = useState([]);
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [external, setExternal] = useState(false);
  const [openGroups, setOpenGroups] = useState({ Vessels: true, Trade: true });

  useEffect(() => {
    async function load() {
      try {
        const session = await fetchAuthSession();
        setEmail(session.tokens?.idToken?.payload?.email ?? "");

        const groups = session.tokens?.idToken?.payload?.["cognito:groups"] || [];
        const isExternal =
          groups.some((g) => EXTERNAL_GROUPS.includes(g)) && !groups.includes("admin") && !groups.includes("ops");
        setExternal(isExternal);
        if (isExternal) {
          setPage("deals");
          return; // outside parties do not load the fleet
        }

        const token = session.tokens?.idToken?.toString();
        const res = await fetch(API_URL, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error(`API returned ${res.status}`);
        setVessels(await res.json());
      } catch (err) {
        console.error("API error:", err);
        setError("Could not load vessels. Please try again.");
      }
    }
    load();
  }, []);

  const go = (id) => { setPage(id); setSelected(null); };
  const toggleGroup = (name) => setOpenGroups({ ...openGroups, [name]: !openGroups[name] });
  const current = ALL_PAGES.find((n) => n.id === page) || ALL_PAGES[0];
  const visibleNav = external ? EXTERNAL_NAV : NAV;
  const inTransit = vessels.filter((v) => v.status === "In Transit").length;
  const anchored = vessels.filter((v) => v.status === "Anchored").length;
  const showDetail = selected && (page === "dashboard" || page === "vessels");

  const navButton = (n, sub) => (
    <button
      key={n.id}
      className={`navbtn ${sub ? "navsub" : ""} ${page === n.id ? "active" : ""}`}
      onClick={() => go(n.id)}
    >
      {n.label}{n.soon && <span className="soon">Soon</span>}
    </button>
  );

  return (
    <div className="app">
      <aside className="sidebar">
        <div className="brand">
          <span className="logo">⚓</span>
          <div><b>MMNY</b><small>Shipping Management Suite</small></div>
        </div>
        <nav>
          {visibleNav.map((n) =>
            n.items ? (
              <div key={n.group}>
                <button
                  className={`navbtn navgroup ${n.items.some((i) => i.id === page) ? "has-active" : ""}`}
                  onClick={() => toggleGroup(n.group)}
                  aria-expanded={!!openGroups[n.group]}
                >
                  <span>{n.group}</span>
                  <span className="navchev">{openGroups[n.group] ? "▾" : "▸"}</span>
                </button>
                {openGroups[n.group] && n.items.map((i) => navButton(i, true))}
              </div>
            ) : (
              navButton(n, false)
            )
          )}
        </nav>
        <button className="signout" onClick={() => signOut()}>Sign out</button>
      </aside>

      <main className="main">
        <header className="topbar">
          <h1>{showDetail ? "Vessel Report" : current.title}</h1>
          <span className="user">{email}</span>
        </header>

        {error && <div className="err">{error}</div>}

        {showDetail && <VesselDetail id={selected} onBack={() => setSelected(null)} />}

        {!showDetail && page === "dashboard" && (
          <>
            <div className="stats">
              <div className="stat"><span>Vessels tracked</span><b>{vessels.length}</b></div>
              <div className="stat"><span>In transit</span><b>{inTransit}</b></div>
              <div className="stat"><span>Anchored</span><b>{anchored}</b></div>
            </div>
            <div className="card">
              <h2>Your fleet</h2>
              <VesselTable vessels={vessels} onOpen={setSelected} />
            </div>
            <div className="card">
              <h2>Quick actions</h2>
              <button className="btn" onClick={() => go("freight")}>Estimate a voyage</button>
            </div>
          </>
        )}

        {!showDetail && page === "vessels" && (
          <div className="card"><VesselTable vessels={vessels} onOpen={setSelected} /></div>
        )}

        {page === "freight" && <Freight />}
        {page === "charter" && <Chartering />}
        {page === "weather" && <WeatherRisk />}
        {page === "ops" && <Trades />}
        {page === "deals" && <Deals />}

        {current.soon && (
          <div className="card empty">
            <h2>{current.label} is coming soon</h2>
            <p>This module is on the roadmap and will appear here.</p>
          </div>
        )}
      </main>
    </div>
  );
}

export default withAuthenticator(App, { hideSignUp: true, components: authComponents });